import Model from "./Model.js";

/**
 * Model for holding the necessary information used during a signing up request
 * for a demo/trial account.
 *
 * @param {string} fistName - Fist name (e.g. John)
 * @param {string} lastName - Last name (e.g. Doe)
 * @param {string} email - E-mail address
 * @param {string} phone - Phone number
 * @param {string} countryId - Country ID
 * @param {string} requestId - Request ID provided by the server for identifing the request
 * @param {string} integrationType - Integration type (e.g. Xero)
 * @param {string} companyName - Company name
 * @param {"InPractice"|"InBusiness"} businessOperationType - ‘InPractice’ or ‘InBusiness’
 * @param {"1-5"|"6-35"|"36+"} companySize - ‘1-5’ or ‘6-35’ or‘36+’
 * @param {string} postcode - company zipcode
 * @param {boolean} allowStoringPersonalData - True if customer allows storage and process of their data
 * @param {boolean} allowBeingContactedForMarketing - True if customer agrees to be contacted
 */
export default class SignUpRequestInfo extends Model {
	constructor({
		firstName,
		lastName,
		phone,
		email,
		countryId,
		requestId,
		integrationType,
		companyName,
		businessOperationType,
		companySize,
		postcode,
		allowStoringPersonalData = false,
		allowBeingContactedForMarketing = false,
	} = {}) {
		super(...arguments);

		this._firstName = firstName;
		this._lastName = lastName;
		this._phone = phone;
		this._email = email;
		this._countryId = countryId;
		this._requestId = requestId;
		this._integrationType = integrationType;
		this._companyName = companyName;
		this._businessOperationType = businessOperationType;
		this._companySize = companySize;
		this._postcode = postcode;
		this._allowStoringPersonalData = allowStoringPersonalData;
		this._allowBeingContactedForMarketing = allowBeingContactedForMarketing;
	}

	getFirstName() {
		return this._firstName;
	}

	setFirstName(newFirstName) {
		this._firstName = newFirstName;
	}

	getLastName() {
		return this._lastName;
	}

	setLastName(newLastName) {
		this._lastName = newLastName;
	}

	getPhone() {
		return this._phone;
	}

	setPhone(newPhone) {
		this._phone = newPhone;
	}

	getEmail() {
		return this._email;
	}

	setEmail(newEmail) {
		this._email = newEmail;
	}

	getCountryId() {
		return this._countryId;
	}

	setCountryId(newCountryId) {
		this._countryId = newCountryId;
	}

	getLanguage() {
		return this._language;
	}

	setLanguage(newLanguage) {
		this._language = newLanguage;
	}

	getIntegrationType() {
		return this._integrationType;
	}

	setIntegrationType(newIntegrationType) {
		this._integrationType = newIntegrationType;
	}

	getRequestId() {
		return this._requestId;
	}

	setRequestId(newRequestId) {
		this._requestId = newRequestId;
	}

	getCompanyName() {
		return this._companyName;
	}

	setCompanyName(newCompanyName) {
		this._companyName = newCompanyName;
	}

	getBusinessOperationType() {
		return this._businessOperationType;
	}

	setBusinessOperationType(newBusinessOperationType) {
		this._businessOperationType = newBusinessOperationType;
	}

	getCompanySize() {
		return this._companySize;
	}

	setCompanySize(newCompanySize) {
		this._companySize = newCompanySize;
	}

	getPostcode() {
		return this._postcode;
	}

	setPostcode(newPostcode) {
		this._postcode = newPostcode;
	}

	getAllowBeingContactedForMarketing() {
		return this._allowBeingContactedForMarketing;
	}

	setAllowBeingContactedForMarketing(allowBeingContactedForMarketing) {
		this._allowBeingContactedForMarketing = allowBeingContactedForMarketing;
	}

	getAllowStoringPersonalData() {
		return this._allowStoringPersonalData;
	}

	setAllowStoringPersonalData(allowStoringPersonalData) {
		this._allowStoringPersonalData = allowStoringPersonalData;
	}

	_serializedProperties() {
		return {
			firstName: this._firstName,
			lastName: this._lastName,
			email: this._email,
			phone: this._phone,
			language: this._language,
			countryId: this._countryId,
			integrationType: this._integrationType,
			requestId: this._requestId,
			companyName: this._companyName,
			businessOperationType: this._businessOperationType,
			companySize: this._companySize,
			postcode: this._postcode,
			allowStoringPersonalData: this._allowStoringPersonalData,
			allowBeingContactedForMarketing:
				this._allowBeingContactedForMarketing,
		};
	}
}
